import React from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { EntitiesService } from '@/services/entities.service';
import { toast } from 'sonner';
import type { EntityResponse } from '@/types/entity.types';

interface DateFilterState {
  // Global date range for ALL transaction pages (using backend format)
  globalDateRange: { start?: string; end?: string } | null;
  
  // Entity data cache for Financial Year calculations (keyed by entityId)
  entityCache: Record<string, EntityResponse>;
  loadingEntities: Set<string>; // Track per-entity loading to prevent race conditions
  
  // Navigation state
  isNavigating: boolean; // Prevent rapid navigation clicks
  
  // Error handling
  errors: Record<string, string>;
  
  // Actions
  setGlobalDateRange: (range: { start?: string; end?: string } | null) => void;
  loadEntityData: (entityId: string) => Promise<void>;
  getCachedEntityData: (entityId: string | null) => EntityResponse | null;
  clearCache: () => void;
  clearErrors: () => void;
  
  // Navigation actions
  navigateNext: () => void;
  navigatePrevious: () => void;
  canNavigateNext: () => boolean;
  canNavigatePrevious: () => boolean;
  
  // Helper functions
  getNavigationStrategy: () => 'monthly' | 'range-size' | 'default';
  getRangeSize: (start: string, end: string) => number;
  getNextRangePreview: () => { start: string; end: string } | null;
  getPreviousRangePreview: () => { start: string; end: string } | null;
  
  // Helper to convert to backend API parameters
  getApiParams: () => { date_from?: string; date_to?: string };
}

const initialState = {
  globalDateRange: null,
  entityCache: {},
  loadingEntities: new Set<string>(),
  isNavigating: false,
  errors: {},
};

// Navigation constants
const MONTH_THRESHOLD = 28; // If range is >= 28 days (shortest month), navigate by month
const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

// UTC Date utilities - all date operations use UTC to avoid timezone issues
const createUTCDate = (year: number, month: number, day: number): Date => {
  return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
};

const parseUTCDateString = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return createUTCDate(year, month - 1, day); // month is 0-indexed in Date constructor
};

const formatUTCDateString = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // month is 0-indexed
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addUTCMonths = (date: Date, months: number): Date => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  
  // Calculate target year and month
  const targetMonth = month + months;
  const targetYear = year + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  
  // Handle month overflow: if original day doesn't exist in target month, use last day of target month
  const lastDayOfTargetMonth = new Date(Date.UTC(targetYear, normalizedMonth + 1, 0)).getUTCDate();
  const targetDay = Math.min(day, lastDayOfTargetMonth);
  
  return createUTCDate(targetYear, normalizedMonth, targetDay);
};

const subUTCMonths = (date: Date, months: number): Date => {
  return addUTCMonths(date, -months);
};

const startOfUTCMonth = (date: Date): Date => {
  return createUTCDate(date.getUTCFullYear(), date.getUTCMonth(), 1);
};

const endOfUTCMonth = (date: Date): Date => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return createUTCDate(year, month, lastDay);
};

const isLastDayOfUTCMonth = (date: Date): boolean => {
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  return date.getUTCDate() === lastDay;
};

// Helper function to validate date format (YYYY-MM-DD)
const isValidDateString = (dateStr: string): boolean => {
  if (!dateStr) return false;
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateStr)) return false;
  
  const date = parseUTCDateString(dateStr);
  return formatUTCDateString(date) === dateStr;
};

export const useDateFilterStore = create<DateFilterState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Set global date range that applies to ALL pages
      setGlobalDateRange: (range: { start?: string; end?: string } | null) => {
        // Validate date format if range is provided
        if (range) {
          if (range.start && !isValidDateString(range.start)) {
            console.error('[DateFilterStore] Invalid start date format:', range.start);
            return;
          }
          if (range.end && !isValidDateString(range.end)) {
            console.error('[DateFilterStore] Invalid end date format:', range.end);
            return;
          }
        }

        // Always clone to ensure a NEW reference even if start/end values are identical.
        // Zustand change detection is reference-based, so without this clone the update
        // would be ignored and downstream hooks (ComplianceContext, etc.) would not
        // re-evaluate when user re-selects the same dates in the picker.
        const clonedRange = range ? { ...range } : null;

        if (import.meta.env.DEV) {
          console.log('[DateFilterStore] Setting global date range (cloned):', clonedRange);
          console.log('[DateFilterStore] Previous range was:', get().globalDateRange);
        }

        set({
          globalDateRange: clonedRange,
          errors: {} // Clear errors on date change
        });

        if (import.meta.env.DEV) {
          console.log('[DateFilterStore] New range set in store:', get().globalDateRange);
        }
      },

      // Load entity data for Financial Year calculations
      loadEntityData: async (entityId: string) => {
        const state = get();
        
        // Check if already cached
        if (state.entityCache[entityId]) {
          return;
        }
        
        // Prevent concurrent requests for same entity
        if (state.loadingEntities.has(entityId)) {
          return;
        }

        set((state) => ({
          loadingEntities: new Set(state.loadingEntities).add(entityId)
        }));
        
        try {
          if (import.meta.env.DEV) {
            console.log('[DateFilterStore] Loading entity data for FY calculations:', entityId);
          }
          const entityData = await EntitiesService.getEntity(entityId, false); // Use cache
          
          set((state) => ({
            entityCache: {
              ...state.entityCache,
              [entityId]: entityData
            },
            loadingEntities: new Set([...state.loadingEntities].filter(id => id !== entityId)),
            errors: {
              ...state.errors,
              [entityId]: '' // Clear any previous errors
            }
          }));
          
          if (import.meta.env.DEV) {
            console.log('[DateFilterStore] Entity data cached for:', entityId, {
              hasFYData: !!(entityData.financial_year_end_month && entityData.financial_year_end_day),
              fyEndMonth: entityData.financial_year_end_month,
              fyEndDay: entityData.financial_year_end_day
            });
          }
        } catch (error) {
          console.error('[DateFilterStore] Failed to load entity data:', error);
          
          set((state) => ({
            loadingEntities: new Set([...state.loadingEntities].filter(id => id !== entityId)),
            errors: {
              ...state.errors,
              [entityId]: error instanceof Error ? error.message : 'Failed to load entity data'
            }
          }));
          
          // Don't show toast for entity data loading errors (graceful degradation)
          // Calendar year will be used as fallback
        } finally {
          // Ensure loading state is cleared even if error occurred
          set((state) => ({
            loadingEntities: new Set([...state.loadingEntities].filter(id => id !== entityId))
          }));
        }
      },

      // Get cached entity data
      getCachedEntityData: (entityId: string | null): EntityResponse | null => {
        if (!entityId) return null;
        return get().entityCache[entityId] || null;
      },

      // Clear entity data cache
      clearCache: () => {
        if (import.meta.env.DEV) {
          console.log('[DateFilterStore] Clearing entity cache');
        }
        set({ entityCache: {}, errors: {} });
      },

      // Clear errors
      clearErrors: () => {
        set({ errors: {} });
      },

      // Convert global date range to backend API parameters
      getApiParams: (): { date_from?: string; date_to?: string } => {
        const { globalDateRange } = get();
        
        if (import.meta.env.DEV) {
          console.log('[DateFilterStore] getApiParams called with globalDateRange:', globalDateRange);
        }
        
        if (!globalDateRange || (!globalDateRange.start && !globalDateRange.end)) {
          if (import.meta.env.DEV) {
            console.log('[DateFilterStore] No global date range set, returning empty params');
          }
          return {};
        }

        const params: { date_from?: string; date_to?: string } = {};
        
        if (globalDateRange.start) {
          params.date_from = globalDateRange.start; // YYYY-MM-DD format
          if (import.meta.env.DEV) {
            console.log('[DateFilterStore] Added date_from:', globalDateRange.start);
          }
        }
        
        if (globalDateRange.end) {
          params.date_to = globalDateRange.end; // YYYY-MM-DD format
          if (import.meta.env.DEV) {
            console.log('[DateFilterStore] Added date_to:', globalDateRange.end);
          }
        }

        if (import.meta.env.DEV) {
          console.log('[DateFilterStore] Generated API params:', params);
        }
        return params;
      },

      // Navigation helper functions (using UTC consistently)
      getRangeSize: (start: string, end: string): number => {
        try {
          const startDate = parseUTCDateString(start);
          const endDate = parseUTCDateString(end);
          
          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            if (import.meta.env.DEV) {
              console.warn('[DateFilterStore] Invalid dates for range size calculation:', start, end);
            }
            return 0;
          }
          
          const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
          return diffDays;
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('[DateFilterStore] Error calculating range size:', error);
          }
          return 0;
        }
      },

      getNextRangePreview: (): { start: string; end: string } | null => {
        try {
          const { globalDateRange, getNavigationStrategy, getRangeSize } = get();
          
          if (!globalDateRange?.start || !globalDateRange?.end) {
            if (import.meta.env.DEV) {
              console.log('[DateFilterStore] getNextRangePreview: No complete date range', globalDateRange);
            }
            return null;
          }
          
          const strategy = getNavigationStrategy();
          if (import.meta.env.DEV) {
            console.log('[DateFilterStore] getNextRangePreview: Strategy =', strategy, 'Range =', globalDateRange);
          }
          
          if (strategy === 'monthly') {
            // Preview monthly navigation
            const startDate = parseUTCDateString(globalDateRange.start);
            const endDate = parseUTCDateString(globalDateRange.end);
            
            const nextStart = addUTCMonths(startDate, 1);
            let nextEnd = addUTCMonths(endDate, 1);
            
            // Preserve last day of month behavior
            if (isLastDayOfUTCMonth(endDate)) {
              nextEnd = endOfUTCMonth(nextEnd);
            }
            
            const result = {
              start: formatUTCDateString(nextStart),
              end: formatUTCDateString(nextEnd)
            };
            if (import.meta.env.DEV) {
              console.log('[DateFilterStore] getNextRangePreview monthly result:', result);
            }
            return result;
          } else if (strategy === 'range-size') {
            // Preview range-size navigation
            const rangeSize = getRangeSize(globalDateRange.start, globalDateRange.end);
            const startDate = parseUTCDateString(globalDateRange.start);
            const endDate = parseUTCDateString(globalDateRange.end);
            
            const nextStart = new Date(startDate.getTime() + (rangeSize * 24 * 60 * 60 * 1000));
            const nextEnd = new Date(endDate.getTime() + (rangeSize * 24 * 60 * 60 * 1000));
            
            const result = {
              start: formatUTCDateString(nextStart),
              end: formatUTCDateString(nextEnd)
            };
            if (import.meta.env.DEV) {
              console.log('[DateFilterStore] getNextRangePreview range-size result:', result);
            }
            return result;
          }
          
          if (import.meta.env.DEV) {
            console.log('[DateFilterStore] getNextRangePreview: No matching strategy, returning null');
          }
          return null;
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('[DateFilterStore] Error calculating next range preview:', error);
          }
          return null;
        }
      },

      getPreviousRangePreview: (): { start: string; end: string } | null => {
        try {
          const { globalDateRange, getNavigationStrategy, getRangeSize } = get();
          
          if (!globalDateRange?.start || !globalDateRange?.end) {
            return null;
          }
          
          const strategy = getNavigationStrategy();
          
          if (strategy === 'monthly') {
            // Preview monthly navigation
            const startDate = parseUTCDateString(globalDateRange.start);
            const endDate = parseUTCDateString(globalDateRange.end);
            
            const prevStart = subUTCMonths(startDate, 1);
            let prevEnd = subUTCMonths(endDate, 1);
            
            // Preserve last day of month behavior
            if (isLastDayOfUTCMonth(endDate)) {
              prevEnd = endOfUTCMonth(prevEnd);
            }
            
            return {
              start: formatUTCDateString(prevStart),
              end: formatUTCDateString(prevEnd)
            };
          } else if (strategy === 'range-size') {
            // Preview range-size navigation
            const rangeSize = getRangeSize(globalDateRange.start, globalDateRange.end);
            const startDate = parseUTCDateString(globalDateRange.start);
            const endDate = parseUTCDateString(globalDateRange.end);
            
            const prevStart = new Date(startDate.getTime() - (rangeSize * 24 * 60 * 60 * 1000));
            const prevEnd = new Date(endDate.getTime() - (rangeSize * 24 * 60 * 60 * 1000));
            
            return {
              start: formatUTCDateString(prevStart),
              end: formatUTCDateString(prevEnd)
            };
          }
          
          return null;
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('[DateFilterStore] Error calculating previous range preview:', error);
          }
          return null;
        }
      },

      getNavigationStrategy: (): 'monthly' | 'range-size' | 'default' => {
        const { globalDateRange, getRangeSize } = get();
        
        if (!globalDateRange) {
          return 'default'; // No range set, default to current month
        }
        
        if (!globalDateRange.start || !globalDateRange.end) {
          return 'default'; // Partial range, default behavior
        }
        
        try {
          const startDate = parseUTCDateString(globalDateRange.start);
          const endDate = parseUTCDateString(globalDateRange.end);
          
          // Validate dates
          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            if (import.meta.env.DEV) {
              console.warn('[DateFilterStore] Invalid dates in navigation strategy:', globalDateRange);
            }
            return 'default';
          }
          
          // Calculate actual duration in days
          const rangeSizeInDays = getRangeSize(globalDateRange.start, globalDateRange.end);
          
          // Check if this represents a complete month or longer period
          // A range is considered "monthly" if it's >= MONTH_THRESHOLD (shortest month)
          // This handles both single complete months and longer periods correctly
          const isMonthlyRange = rangeSizeInDays >= MONTH_THRESHOLD;
          
          if (import.meta.env.DEV) {
            console.log('[DateFilterStore] Navigation strategy calculation:', {
              range: globalDateRange,
              rangeSizeInDays,
              strategy: isMonthlyRange ? 'monthly' : 'range-size'
            });
          }
          
          return isMonthlyRange ? 'monthly' : 'range-size';
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('[DateFilterStore] Error calculating navigation strategy:', error);
          }
          return 'default';
        }
      },

      canNavigateNext: (): boolean => {
        const { globalDateRange, isNavigating } = get();
        
        if (isNavigating) return false;
        
        // Always allow navigation if no range or if we have a valid range
        if (!globalDateRange || (!globalDateRange.start && !globalDateRange.end)) {
          return true; // Can navigate to current month + 1
        }
        
        // Check if navigation would exceed MAX_YEAR by simulating next navigation
        try {
          const endDate = globalDateRange.end 
            ? parseUTCDateString(globalDateRange.end)
            : parseUTCDateString(globalDateRange.start);
          
          // Simulate next navigation to check if it would exceed MAX_YEAR
          const strategy = get().getNavigationStrategy();
          let simulatedNextDate: Date;
          
          if (strategy === 'monthly') {
            simulatedNextDate = addUTCMonths(endDate, 1);
          } else {
            // For range-size or default, add at least 1 day to check boundary
            simulatedNextDate = new Date(endDate.getTime() + (1 * 24 * 60 * 60 * 1000));
          }
          
          return simulatedNextDate.getUTCFullYear() <= MAX_YEAR;
        } catch {
          return false;
        }
      },

      canNavigatePrevious: (): boolean => {
        const { globalDateRange, isNavigating } = get();
        
        if (isNavigating) return false;
        
        // Always allow navigation if no range or if we have a valid range
        if (!globalDateRange || (!globalDateRange.start && !globalDateRange.end)) {
          return true; // Can navigate to current month - 1
        }
        
        // Check if navigation would go below MIN_YEAR by simulating previous navigation
        try {
          const startDate = globalDateRange.start 
            ? parseUTCDateString(globalDateRange.start)
            : parseUTCDateString(globalDateRange.end);
          
          // Simulate previous navigation to check if it would go below MIN_YEAR
          const strategy = get().getNavigationStrategy();
          let simulatedPrevDate: Date;
          
          if (strategy === 'monthly') {
            simulatedPrevDate = subUTCMonths(startDate, 1);
          } else {
            // For range-size or default, subtract at least 1 day to check boundary
            simulatedPrevDate = new Date(startDate.getTime() - (1 * 24 * 60 * 60 * 1000));
          }
          
          
          return simulatedPrevDate.getUTCFullYear() >= MIN_YEAR;
        } catch {
          return false;
        }
      },

      navigateNext: () => {
        const { globalDateRange, getNavigationStrategy, setGlobalDateRange, canNavigateNext, getRangeSize } = get();
        
        if (!canNavigateNext()) {
          if (import.meta.env.DEV) {
            console.warn('[DateFilterStore] Cannot navigate next');
          }
          return;
        }

        set({ isNavigating: true });

        try {
          const strategy = getNavigationStrategy();
          if (import.meta.env.DEV) {
            console.log('[DateFilterStore] Navigation strategy for next:', strategy);
          }

          if (strategy === 'default') {
            // No range set, navigate to next month from current (using UTC)
            const currentDate = new Date();
            const currentUTC = createUTCDate(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate());
            const nextMonthUTC = addUTCMonths(currentUTC, 1);
            const monthStart = startOfUTCMonth(nextMonthUTC);
            const monthEnd = endOfUTCMonth(nextMonthUTC);
            
            setGlobalDateRange({
              start: formatUTCDateString(monthStart),
              end: formatUTCDateString(monthEnd)
            });
          } else if (strategy === 'monthly' && globalDateRange) {
            // Shift both start and end dates by 1 month using UTC operations
            const startDate = parseUTCDateString(globalDateRange.start);
            const endDate = parseUTCDateString(globalDateRange.end);
            
            // Always shift start date by 1 month
            const nextStart = addUTCMonths(startDate, 1);
            
            // For end date: if it's last day of its month, preserve that property in target month
            let nextEnd = addUTCMonths(endDate, 1);
            if (isLastDayOfUTCMonth(endDate)) {
              // Original end was last day of month, ensure result is last day of target month
              nextEnd = endOfUTCMonth(nextEnd);
            }
            
            if (import.meta.env.DEV) {
              console.log('[DateFilterStore] Monthly navigation next (shift by 1 month):', {
                strategy,
                currentRange: globalDateRange,
                rangeSize: getRangeSize(globalDateRange.start, globalDateRange.end),
                newRange: { start: formatUTCDateString(nextStart), end: formatUTCDateString(nextEnd) }
              });
            }
            
            setGlobalDateRange({
              start: formatUTCDateString(nextStart),
              end: formatUTCDateString(nextEnd)
            });
          } else if (strategy === 'range-size' && globalDateRange) {
            // Shift the same range forward by the range size for ranges < 28 days
            const rangeSize = getRangeSize(globalDateRange.start, globalDateRange.end);
            const startDate = parseUTCDateString(globalDateRange.start);
            const endDate = parseUTCDateString(globalDateRange.end);
            
            const nextStart = new Date(startDate.getTime() + (rangeSize * 24 * 60 * 60 * 1000));
            const nextEnd = new Date(endDate.getTime() + (rangeSize * 24 * 60 * 60 * 1000));
            
            if (import.meta.env.DEV) {
              console.log('[DateFilterStore] Range-size navigation next (shift):', {
                strategy,
                currentRange: globalDateRange,
                rangeSize,
                newRange: { start: formatUTCDateString(nextStart), end: formatUTCDateString(nextEnd) }
              });
            }
            
            setGlobalDateRange({
              start: formatUTCDateString(nextStart),
              end: formatUTCDateString(nextEnd)
            });
          }
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('[DateFilterStore] Error during next navigation:', error);
          }
          toast.error('Failed to navigate to next period');
        } finally {
          set({ isNavigating: false });
        }
      },

      navigatePrevious: () => {
        const { globalDateRange, getNavigationStrategy, setGlobalDateRange, canNavigatePrevious, getRangeSize } = get();
        
        if (!canNavigatePrevious()) {
          if (import.meta.env.DEV) {
            console.warn('[DateFilterStore] Cannot navigate previous');
          }
          return;
        }

        set({ isNavigating: true });

        try {
          const strategy = getNavigationStrategy();
          if (import.meta.env.DEV) {
            console.log('[DateFilterStore] Navigation strategy for previous:', strategy);
          }

          if (strategy === 'default') {
            // No range set, navigate to previous month from current (using UTC)
            const currentDate = new Date();
            const currentUTC = createUTCDate(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate());
            const prevMonthUTC = subUTCMonths(currentUTC, 1);
            const monthStart = startOfUTCMonth(prevMonthUTC);
            const monthEnd = endOfUTCMonth(prevMonthUTC);
            
            setGlobalDateRange({
              start: formatUTCDateString(monthStart),
              end: formatUTCDateString(monthEnd)
            });
          } else if (strategy === 'monthly' && globalDateRange) {
            // Shift both start and end dates back by 1 month using UTC operations
            const startDate = parseUTCDateString(globalDateRange.start);
            const endDate = parseUTCDateString(globalDateRange.end);
            
            // Always shift start date by 1 month
            const prevStart = subUTCMonths(startDate, 1);
            
            // For end date: if it's last day of its month, preserve that property in target month
            let prevEnd = subUTCMonths(endDate, 1);
            if (isLastDayOfUTCMonth(endDate)) {
              // Original end was last day of month, ensure result is last day of target month
              prevEnd = endOfUTCMonth(prevEnd);
            }
            
            if (import.meta.env.DEV) {
              console.log('[DateFilterStore] Monthly navigation previous (shift by 1 month):', {
                strategy,
                currentRange: globalDateRange,
                rangeSize: getRangeSize(globalDateRange.start, globalDateRange.end),
                newRange: { start: formatUTCDateString(prevStart), end: formatUTCDateString(prevEnd) }
              });
            }
            
            setGlobalDateRange({
              start: formatUTCDateString(prevStart),
              end: formatUTCDateString(prevEnd)
            });
          } else if (strategy === 'range-size' && globalDateRange) {
            // Shift the same range backward by the range size for ranges < 28 days
            const rangeSize = getRangeSize(globalDateRange.start, globalDateRange.end);
            const startDate = parseUTCDateString(globalDateRange.start);
            const endDate = parseUTCDateString(globalDateRange.end);
            
            const prevStart = new Date(startDate.getTime() - (rangeSize * 24 * 60 * 60 * 1000));
            const prevEnd = new Date(endDate.getTime() - (rangeSize * 24 * 60 * 60 * 1000));
            
            if (import.meta.env.DEV) {
              console.log('[DateFilterStore] Range-size navigation previous (shift):', {
                strategy,
                currentRange: globalDateRange,
                rangeSize,
                newRange: { start: formatUTCDateString(prevStart), end: formatUTCDateString(prevEnd) }
              });
            }
            
            setGlobalDateRange({
              start: formatUTCDateString(prevStart),
              end: formatUTCDateString(prevEnd)
            });
          }
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('[DateFilterStore] Error during previous navigation:', error);
          }
          toast.error('Failed to navigate to previous period');
        } finally {
          set({ isNavigating: false });
        }
      },
    }),
    {
      name: 'global-date-filter',
      partialize: (state) => ({
        // Only persist the date range, not cache or loading states
        globalDateRange: state.globalDateRange,
      }),
    }
  )
);

// Convenience hooks for easier usage
export const useGlobalDateRange = () => {
  const store = useDateFilterStore();
  return {
    dateRange: store.globalDateRange,
    setDateRange: store.setGlobalDateRange,
    getApiParams: store.getApiParams,
  };
};

// Navigation hook for month controls
export const useGlobalDateNavigation = () => {
  const store = useDateFilterStore();
  return {
    navigateNext: store.navigateNext,
    navigatePrevious: store.navigatePrevious,
    canNavigateNext: store.canNavigateNext(),
    canNavigatePrevious: store.canNavigatePrevious(),
    isNavigating: store.isNavigating,
    navigationStrategy: store.getNavigationStrategy(),
    getNextRangePreview: store.getNextRangePreview,
    getPreviousRangePreview: store.getPreviousRangePreview,
  };
};

export const useEntityDataForDateFilter = (entityId: string | null) => {
  const store = useDateFilterStore();
  
  // Auto-load entity data when entityId changes
  React.useEffect(() => {
    if (entityId && !store.entityCache[entityId]) {
      store.loadEntityData(entityId);
    }
  }, [entityId, store]);

  return {
    entityData: store.getCachedEntityData(entityId),
    isLoading: entityId ? store.loadingEntities.has(entityId) : false,
    error: entityId ? store.errors[entityId] : null,
  };
};

// Export store for direct access when needed
export { useDateFilterStore as default };